import { hotkeys } from 'react-keyboard-shortcuts';
import { Typography } from '@material-ui/core';
import IconButton from '@material-ui/core/IconButton';
import { withStyles } from '@material-ui/core/styles';
import Tooltip from '@material-ui/core/Tooltip';
import withWidth from '@material-ui/core/withWidth';
import ArrowUpwardIcon from '@material-ui/icons/ArrowUpward';
import FullscreenIcon from '@material-ui/icons/Fullscreen';
import FullscreenExitIcon from '@material-ui/icons/FullscreenExit';
import InfoIcon from '@material-ui/icons/Info';
import UnfoldLessIcon from '@material-ui/icons/UnfoldLess';
import VerticalSplitIcon from '@material-ui/icons/VerticalSplit';
import PropTypes from 'prop-types';
import React, { Component } from 'react';
import { ENTITLEMENTS } from '../application-core/constants';
import GetStarted from '../application-core/GetStarted';
import ActionText from '../core/ActionText';
import CreateButton from '../core/CreateButton';
import ProjectLanguageSelector from '../core/ProjectLanguageSelector';
import { globalStyles } from '../core/style/gstheme';
import { checkResourceEntitlements } from '../core/utils/utils';
import withGS from '../core/withGShoc';
import ClassData from './ClassData';
import CreateNodeDialogContainer from './CreateNodeDialogContainer';
import CreateResourceDialog from './CreateResourceDialog';
import ResourceSearch from './ResourceSearch';
import ResourceStructure from './ResourceStructure';
import { PANEL_WIDTH_NORMAL, PANEL_WIDTH_WIDE } from '../core/utils/constants';

const styles = Object.assign({}, globalStyles);

function ClassCreateDialog(props) {

  const iriOptions = Object.assign({}, props.iriOptions);
  delete iriOptions.initialLowerCase;

  return (
    <CreateNodeDialogContainer
      ontologyIRI={props.ontologyIRI}
      useSlashIRI={props.useSlashIRI}
      useUuidIRI={props.useUuidIRI}
      iriOptions={iriOptions}
      onlyComponentResources={true}
      useWorkflow={props.useWorkflow}
      parentIRI="http://www.w3.org/2002/07/owl#Thing"
      button={props.bigButton ? <CreateButton label="Add" /> : null}
      onAddExistingNode={props.onAddExistingClass}
      onAddNewNode={props.onAddNewClass(props.titlePath, "")}
      graph={props.graph}
      disableMultiple={props.disableMultiple}
      searchClass="http://www.w3.org/2002/07/owl#Class"
      searchClassFilter={[
        "http://www.w3.org/2002/07/owl#Class",
        "http://www.w3.org/2002/07/owl#DatatypeProperty",
        "http://www.w3.org/2002/07/owl#ObjectProperty",
        "http://www.w3.org/2002/07/owl#AnnotationProperty",
        "http://www.w3.org/2002/07/owl#NamedIndividual",
        "http://www.w3.org/2000/01/rdf-schema#Datatype"
      ]}
      existing={props.data.hasSubClass}
      exclude={[
        "https://graphifi.com/schema/xowl/topAnnotationProperty",
        "http://www.w3.org/2002/07/owl#topObjectProperty",
        "http://www.w3.org/2002/07/owl#topDataProperty",
        "http://www.w3.org/2002/07/owl#Thing"
      ]}
      createDialog={CreateResourceDialog}
      title="Add Class"
      placeholder="Enter class label"
      name="Add a top class (Alt+n)"
    />
  );
}

class ClassView extends Component {

  constructor(props) {
    super(props);
    this.infoRef = React.createRef();
    this.controlRef = React.createRef();
  }

  hot_keys = {
    'alt+i': {
      priority: 1,
      handler: () => {
        if (this.infoRef && this.infoRef.current) {
          this.infoRef.current.focus();
        }
      }
    },
    'alt+c': {
      priority: 1,
      handler: () => {
        if (this.controlRef && this.controlRef.current) {
          this.controlRef.current.focus();
        }
      }
    }
  };

  componentDidMount() {
    if (this.props.activeClass && this.props.data.hasSubClass)
      this.props.onFoundClass(this.props.data.hasSubClass)(this.props.activeClass);
  }

  excludeByPathFunction = (id) => {
    let ebp = [{
      predicate: [
        "https://graphifi.com/schema/xowl/hasSubClass"
      ],
      target: id
    },
    {
      predicate: [
        "http://www.w3.org/2000/01/rdf-schema#subClassOf"
      ],
      target: id
    },
    {
      predicate: [
        "http://www.w3.org/2002/07/owl#equivalentClass"
      ],
      target: id
    },
    {
      predicate: [
        "http://www.w3.org/2002/07/owl#equivalentClass"
      ],
      reverse: true,
      target: id
    },
    {
      predicate: [
        "http://www.w3.org/2002/07/owl#disjointWith",
        "https://graphifi.com/schema/xowl/hasSubClass"
      ],
      target: id,
      pathOperators: ["", "*"]
    },
    {
      predicate: [
        "http://www.w3.org/2002/07/owl#disjointWith",
        "https://graphifi.com/schema/xowl/hasSubClass"
      ],
      target: id,
      reverse: true,
      pathOperators: ["", "*"]
    },
    {
      predicate: [
        "http://www.w3.org/2000/01/rdf-schema#subClassOf",
        "http://www.w3.org/2002/07/owl#disjointWith"
      ],
      target: id,
      reverse: true,
      pathOperators: ["*", ""]
    }];
    ebp.push({
      predicate: [
        "https://graphifi.com/schema/xowl/hasSubClass"
      ],
      target: id,
      pathOperators: ["+"]
    });
    return ebp;
  };

  render() {

    const { activeClass, classes, ontologyIRI, onSelect, onAddNewClass, onAddExistingClass, viewWidth, useSlashIRI, useUuidIRI, hiddenProps,
      expandNode, collapseNode, selectedTitlePath, viewLang, onSelectViewLanguage, width, onFullViewChange, onViewWidthChange, isFullView,
      data, appBar, foundRef, defaultLang, foundPath, onFoundClass, buildLink, graph, titlePath, onCollapse, editingOptions, advancedView,
      foundTime, onDeleteClass, expandedPaths, project, onBreakLink, availableAnnotationProperty, entitlements, projectUsers,
      excludeDeprecated, useWorkflow } = this.props;

    const isReadOnly = checkResourceEntitlements(data, ENTITLEMENTS.EDIT_ONTOLOGY);
    const isLargeScreen = width === "lg" || width === "xl";
    const iriOptions = Object.assign({}, this.props.iriOptions);
    delete iriOptions.initialLowerCase;
    const panelWidth = advancedView ? PANEL_WIDTH_WIDE : PANEL_WIDTH_NORMAL;

    return (
      <div>
        <div
          className={classes.controlPanel}
          style={{
            ...(!isFullView && advancedView && viewWidth === 0) && { width: panelWidth },
            ...(isFullView) && { width: 'calc(100% - 100px)' },
            ...(!isFullView && isLargeScreen && viewWidth !== 0) && { width: (panelWidth + (viewWidth * 100)) }
          }}>
          <div className={classes.views}>
            {appBar}
          </div>
          <div className={classes.controlPanelInner}>
            <div className={classes.controlPanelAdd}>
              {!isReadOnly && data.hasSubClass && data.hasSubClass.length > 0 ?
                <ClassCreateDialog {...this.props} />
                :
                <span style={{ width: 30 }}>&nbsp;</span>}
            </div>
            <div className={advancedView ? classes.controlPanelSearchWide : classes.controlPanelSearch}>
              {data.hasSubClass && data.hasSubClass.length > 0 &&
                <ResourceSearch
                  searchClass="http://www.w3.org/2002/07/owl#Class"
                  graph={graph}
                  ontologyIRI={ontologyIRI}
                  title="Find a class"
                  onFoundResource={onFoundClass(data.hasSubClass)}
                  defaultLang={viewLang || defaultLang} />
              }
            </div>
          </div>
          <div data-test="class-tree" tabIndex={0} ref={this.controlRef}
            className={classes.deepTree}
            style={
              {
                ...(advancedView && viewWidth === 0) && { width: panelWidth },
                ...(isFullView) && { width: 'calc(100% - 100px)' },
                ...(!isFullView && isLargeScreen && viewWidth !== 0) && { width: (panelWidth + (viewWidth * 100)) }
              }
            }>
            {data.hasSubClass && data.hasSubClass.length > 0 &&
              <div className={advancedView ? classes.treeOptionsWide : classes.treeOptions}>
                <div data-test="editing-mode" style={{ paddingTop: 4, paddingBottom: 4, overflow: 'auto' }}>
                  {excludeDeprecated &&
                    <Tooltip title={<span data-test="deprecated-message">{"Deprecated classes are hidden"}</span>}>
                      <span data-test={"deprecated-hidden"} onClick={onCollapse} style={{ float: 'right', marginLeft: 4, color: '#CA4F0C' }}>
                        <InfoIcon style={{ transform: 'translateY(3px)' }} />
                      </span>
                    </Tooltip>
                  }
                  <Tooltip title="Collapse all">
                    <span data-test={"collapse-all"} onClick={onCollapse} style={{ float: 'right', marginLeft: 4 }}>
                      <IconButton data-test={"option-collapse-all"} size="small">
                        <UnfoldLessIcon />
                      </IconButton>
                    </span>
                  </Tooltip>
                  <Tooltip title="Toggle full view mode">
                    <span data-test={"full-view"} onClick={onFullViewChange} style={{ float: 'right', marginLeft: 4 }}>
                      <IconButton data-test={"option-full-view"} size="small">
                        {isFullView ?
                          <FullscreenExitIcon />
                          :
                          <FullscreenIcon />
                        }
                      </IconButton>
                    </span>
                  </Tooltip>
                  {isLargeScreen &&
                    <Tooltip title="Change hierarchy width">
                      <span data-test={"split-view-" + viewWidth} onClick={onViewWidthChange} style={{ float: 'right' }}>
                        <IconButton data-test={"option-split"} size="small">
                          <VerticalSplitIcon />
                        </IconButton>
                      </span>
                    </Tooltip>
                  }
                </div>
              </div>
            }
            {data.hasSubClass && data.hasSubClass.length > 0 ?
              <ResourceStructure
                filter={true}
                goToResource={onFoundClass(data.hasSubClass)}
                defaultLang={defaultLang}
                graph={graph}
                foundPath={foundPath}
                useWorkflow={useWorkflow}
                activeResource={activeClass}
                excludeDeprecated={excludeDeprecated}
                foundRef={foundRef}
                buildLink={buildLink}
                iriOptions={iriOptions}
                isReadOnly={isReadOnly}
                onBreakLink={onBreakLink}
                foundTime={foundTime}
                titlePath={titlePath}
                titleTree={[]}
                viewLang={viewLang}
                selectedTitlePath={selectedTitlePath}
                childProperty="hasSubClass"
                expandedPaths={expandedPaths}
                onAddExistingNode={onAddExistingClass}
                onAddNewNode={onAddNewClass}
                onDeleteNode={onDeleteClass}
                expandNode={expandNode}
                collapseNode={collapseNode}
                ontologyIRI={ontologyIRI}
                useSlashIRI={useSlashIRI}
                useUuidIRI={useUuidIRI}
                data={data}
                path=""
                onSelect={onSelect}
                permissions={[ENTITLEMENTS.EDIT_ONTOLOGY]}
                searchClass="http://www.w3.org/2002/07/owl#Class"
                excludeByPath={this.excludeByPathFunction}
                searchClassFilter={[
                  "http://www.w3.org/2002/07/owl#Class",
                  "http://www.w3.org/2002/07/owl#DatatypeProperty",
                  "http://www.w3.org/2002/07/owl#ObjectProperty",
                  "http://www.w3.org/2002/07/owl#AnnotationProperty",
                  "http://www.w3.org/2002/07/owl#NamedIndividual",
                  "http://www.w3.org/2000/01/rdf-schema#Datatype"
                ]}
                createDialog={CreateResourceDialog}
                addTitle="Add Subclass"
                emptyLevel="No classes."
              />
              :
              isReadOnly ?
                <ActionText offset="85px">No classes.</ActionText>
                :
                <GetStarted text="Add a class to get started." action={
                  <ClassCreateDialog bigButton={true} disableMultiple={true} {...this.props} />
                } />
            }
          </div>
        </div>
        <div data-test="language-panel" className={classes.viewLanguagePanel}>
          <ProjectLanguageSelector selectedLang={viewLang} includeLabel={false} onSelect={onSelectViewLanguage} />
        </div>
        {!isFullView &&
          <div data-test="info-panel" ref={this.infoRef} tabIndex={0}
            className={classes.infoPanel}
            style={
              {
                ...(!isFullView && viewWidth === 0) && { left: panelWidth + 40 },
                ...(!isFullView && viewWidth !== 0) && { left: ((panelWidth + 40) + (viewWidth * 100)) }
              }
            }>
            {
              data.hasSubClass && data.hasSubClass.length > 0 ?
                (activeClass === null ?
                  <ActionText offset="180px">Select a class to view the detail.</ActionText>
                  :
                  <ClassData
                    buildLink={buildLink}
                    availableAnnotationProperty={availableAnnotationProperty}
                    entitlements={entitlements}
                    projectUsers={projectUsers}
                    key={activeClass}
                    useWorkflow={useWorkflow}
                    excludeDeprecated={excludeDeprecated}
                    project={project}
                    graph={graph}
                    hiddenProps={hiddenProps}
                    editingOptions={editingOptions}
                    onFoundResource={onFoundClass(data.hasSubClass)}
                    classIRI={activeClass}
                    viewLang={viewLang}
                    ontologyIRI={ontologyIRI}
                    onDeleteClass={onDeleteClass} />
                )
                :
                <div>
                  <div data-test="hint-1" style={{ margin: '6px 12px', backgroundColor: '#f5f5f5', padding: '0px 12px 6px 12px', boxShadow: '0px 3px 6px #00000029', borderRadius: 4, border: 'solid 1px #f0f0f0', width: 180 }}>
                    <Typography variant="caption">Click on the ontology title <ArrowUpwardIcon style={{ transform: 'translate(0px, 6px)' }} /> or the settings icon to manage the ontology.</Typography>
                  </div>
                  <ActionText offset="180px">When classes are created you will be able to view the detail here.</ActionText>
                </div>
            }
          </div>
        }
      </div>
    );
  }
}

ClassView.defaultProps = {
  viewWidth: 0,
  advancedView: false
};

ClassView.propTypes = {
  classes: PropTypes.object,
  buildLink: PropTypes.func.isRequired,
  defaultLang: PropTypes.string.isRequired,
  entitlements: PropTypes.array.isRequired,
  projectUsers: PropTypes.array,
  ontologyIRI: PropTypes.string.isRequired,
  useSlashIRI: PropTypes.bool,
  useUuidIRI: PropTypes.bool,
  activeClass: PropTypes.string,
  graph: PropTypes.string.isRequired,
  titlePath: PropTypes.array,
  expandNode: PropTypes.func.isRequired,
  collapseNode: PropTypes.func.isRequired,
  onAddExistingClass: PropTypes.func.isRequired,
  onAddNewClass: PropTypes.func.isRequired,
  onFoundClass: PropTypes.func.isRequired,
  onSelectViewLanguage: PropTypes.func,
  excludeDeprecated: PropTypes.bool,
  viewLang: PropTypes.string,
  onDeleteClass: PropTypes.func.isRequired,
  onBreakLink: PropTypes.func.isRequired,
  selectedTitlePath: PropTypes.array,
  foundPath: PropTypes.string,
  foundTime: PropTypes.number,
  project: PropTypes.string.isRequired,
  onSelect: PropTypes.func.isRequired,
  useWorkflow: PropTypes.bool,
  isFullView: PropTypes.bool,
  viewWidth: PropTypes.number,
  onCollapse: PropTypes.func,
  onFullViewChange: PropTypes.func.isRequired,
  onViewWidthChange: PropTypes.func.isRequired,
  data: PropTypes.object.isRequired,
  availableAnnotationProperty: PropTypes.array,
  hiddenProps: PropTypes.array,
  editingOptions: PropTypes.array,
  advancedView: PropTypes.bool,
  iriOptions: PropTypes.object
};

export default withWidth()(withStyles(styles)(withGS(hotkeys(ClassView))));